import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import {
  useInvalidateTouchpointVersions,
  usePatchTouchpointVersionStatus,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";

import { TouchpointStatuses } from "types";

interface ApproveAsClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVersion?: TouchpointVersionAttributes;
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
}

export const ApproveAsClientModal = ({
  isOpen,
  onClose,
  selectedVersion,
  setSelectedVersion,
}: ApproveAsClientModalProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const invalidateTouchpointVersions = useInvalidateTouchpointVersions(versionScope);
  const patchStatusMutation = usePatchTouchpointVersionStatus(versionScope);

  const approveTouchpoint = async (): Promise<boolean> => {
    try {
      if (
        !currentClient?.id ||
        !currentCampaign?.id ||
        !currentTouchpoint?.id ||
        !selectedVersion?.id
      ) {
        throw new Error("Missing required information to approve touchpoint.");
      }
      await patchStatusMutation.mutateAsync({
        id: selectedVersion.id,
        status: TouchpointStatuses.APPROVED,
      });

      await invalidateTouchpointVersions();

      return true;
    } catch (err) {
      console.error("Error in approveTouchpoint:", err);
      return false;
    }
  };

  const approveTouchpointAsClient = async (
    selectedVersion?: TouchpointVersionAttributes,
  ): Promise<void> => {
    const isApproved = await approveTouchpoint();

    if (isApproved && selectedVersion) {
      setSelectedVersion({
        ...selectedVersion,
        status: TouchpointStatuses.APPROVED,
      });

      onClose();
      toast.success({ data: { title: "Completed approval" } });
    } else {
      // If it fails, show a generic error message to the user.
      // A console.error message will be logged by approveTouchpoint()
      toast.error({
        data: {
          title: "Failed to approve creative",
        },
        autoClose: false,
      });
    }
  };

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onClose}
      intent="warning"
      headline="Are you sure you want to approve as client?"
      confirmButtonText="Approve as client"
      onConfirm={() => approveTouchpointAsClient(selectedVersion)}
    />
  );
};
